import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { useAnual } from '#components/mobile/anual/useAnual';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { Boton } from '#components/mobile/ui/Boton';
import { CampoImporte } from '#components/mobile/ui/CampoImporte';
import { EstadoVacio } from '#components/mobile/ui/EstadoVacio';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio, num, texto } from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import { BotonRepartir } from './BotonRepartir';
import { CabeceraNomina } from './comun';
import { fechaCobro, importePaga, ponerDestino, sinDecidir } from './nomina';
import type { DestinoPaga, PlanPaga } from './nomina';
import { mayuscula, nombrePaga } from './textos';
import { useConfigNomina, usePlanesPagas } from './useNomina';

const PLAN_VACIO: PlanPaga = { destinos: [], estado: 'pendiente' };

/**
 * «¿Qué hará tu paga?» (`/pagas/:clave`, maqueta 3): a qué va cada parte de
 * una paga extra ya cobrada. Ideas de Rumbo: lo que falta en «Gasto anual» o
 * dejarlo en «Listo para asignar». «Repartir» lo asigna en el mes del cobro.
 * Una paga que aún no ha llegado no se planea (Vadik, 20-oct-2026).
 */
export function PlanPagaPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { clave = '' } = useParams<{ clave: string }>();
  const { config } = useConfigNomina();
  const { planes } = usePlanesPagas();
  const { data: { list: categorias } = { list: [] } } = useCategories();
  const { filas: anuales } = useAnual();
  const [plan, setPlan] = useState<PlanPaga | null>(null);

  const [idPaga, anoTexto] = clave.split('@');
  const paga = config?.pagas.find(p => p.id === idPaga);

  // El plan guardado llega un instante después de abrir: se copia una vez.
  useEffect(() => {
    if (plan == null && config) {
      setPlan(planes[clave] ?? PLAN_VACIO);
    }
  }, [plan, planes, clave, config]);

  if (!config || !paga || !anoTexto) {
    return (
      <Page padding={0} header={<CabeceraNomina titulo={t('Extra payment')} />}>
        <EstadoVacio
          titulo={t('This extra payment no longer exists')}
          texto={t('Check your payroll in Settings.')}
        />
      </Page>
    );
  }

  const ano = Number(anoTexto);
  const importe = importePaga(config, paga);
  const fecha = fechaCobro(ano, paga.mes, paga.dia);
  if (fecha > monthUtils.currentDay()) {
    return (
      <Page padding={0} header={<CabeceraNomina titulo={t('Extra payment')} />}>
        <EstadoVacio
          titulo={t('This payment has not arrived yet')}
          texto={t(
            'When it arrives, Rumbo will let you know and help you share it out.',
          )}
        />
      </Page>
    );
  }
  const actual = plan ?? PLAN_VACIO;
  const falta = sinDecidir(importe, actual);
  const nombre = nombrePaga(t, paga, locale);

  const poner = (destino: DestinoPaga) =>
    setPlan(ponerDestino(actual, destino, importe));

  const nombreDestino = (d: DestinoPaga) =>
    d.tipo === 'por-asignar'
      ? t('Ready to Assign')
      : (categorias.find(c => c.id === d.id)?.name ?? t('Deleted category'));

  const enPlan = new Set(
    actual.destinos.flatMap(d => (d.tipo === 'categoria' ? [d.id] : [])),
  );
  const ideasAnuales = anuales
    .filter(f => f.faltaTotal > 0 && !enPlan.has(f.id))
    .sort((a, b) => (a.vence ?? '9999').localeCompare(b.vence ?? '9999'))
    .slice(0, 3);
  const conPorAsignar = actual.destinos.some(d => d.tipo === 'por-asignar');

  const otraCategoria = () =>
    dispatch(
      pushModal({
        modal: {
          name: 'category-autocomplete',
          options: {
            title: t('What will part of this payment go to?'),
            onSelect: id => {
              if (id) {
                poner({ tipo: 'categoria', id, importe: falta });
              }
            },
          },
        },
      }),
    );

  return (
    <Page
      padding={0}
      header={
        <CabeceraNomina
          titulo={t('What will your payment do?')}
          subtitulo={
            <PrivacyFilter>
              {`${mayuscula(nombre)} · ${format(importe, 'financial')} · ${monthUtils.format(fecha, "d 'de' MMMM", locale)}`}
            </PrivacyFilter>
          }
        />
      }
    >
      <View
        data-testid="plan-paga"
        style={{
          gap: espacio.tarjetas,
          padding: `0 ${espacio.margen}px`,
          paddingBottom: MOBILE_NAV_HEIGHT + espacio.margen + 72,
          flexShrink: 0,
        }}
      >
        {actual.destinos.length > 0 ? (
          <Tarjeta relleno={0}>
            {actual.destinos.map((d, i) => (
              <View
                key={d.tipo === 'categoria' ? d.id : d.tipo}
                style={{ borderTop: i === 0 ? 0 : `1px solid ${color.line}` }}
              >
                <ImporteDestino
                  etiqueta={nombreDestino(d)}
                  importe={d.importe}
                  onCambiar={nuevo => poner({ ...d, importe: nuevo })}
                />
              </View>
            ))}
          </Tarjeta>
        ) : (
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            <Trans>
              Decide what this money will do, so it does not vanish.
            </Trans>
          </Text>
        )}

        <Text style={{ ...texto.etiqueta, color: color.fg2 }}>
          <Trans>Ideas from Rumbo</Trans>
        </Text>
        <View style={{ gap: 8 }}>
          {ideasAnuales.map(f => {
            const pon = Math.min(f.faltaTotal, falta);
            return (
              <Idea
                key={f.id}
                titulo={f.nombre}
                detalle={t('Annual expense · {{amount}} missing', {
                  amount: format(f.faltaTotal, 'financial'),
                })}
                importe={pon}
                onPress={() =>
                  poner({ tipo: 'categoria', id: f.id, importe: pon })
                }
              />
            );
          })}
          {!conPorAsignar && (
            <Idea
              titulo={t('Ready to Assign')}
              detalle={t(
                'It stays unassigned and you give it a job whenever you want',
              )}
              importe={falta}
              onPress={() => poner({ tipo: 'por-asignar', importe: falta })}
            />
          )}
          <Boton variante="fantasma" onPress={otraCategoria}>
            <Trans>Another category</Trans>
          </Boton>
        </View>
      </View>

      <View
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: MOBILE_NAV_HEIGHT,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          padding: `12px ${espacio.margen}px`,
          backgroundColor: color.surface,
          boxShadow: '0 -6px 20px rgba(20,30,60,.08)',
        }}
      >
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 12, fontWeight: 700, color: color.warn }}>
            <Trans>Undecided</Trans>
          </Text>
          <Text
            style={{ ...texto.titulo, ...num, color: color.warn }}
            data-testid="plan-paga-sin-decidir"
          >
            <PrivacyFilter>{format(falta, 'financial')}</PrivacyFilter>
          </Text>
        </View>
        <BotonRepartir
          clave={clave}
          mes={fecha.slice(0, 7)}
          plan={actual}
          onHecho={() => void navigate(-1)}
        />
      </View>
    </Page>
  );
}

function Idea({
  titulo,
  detalle,
  importe,
  onPress,
}: {
  titulo: string;
  detalle: string;
  importe: number;
  onPress: () => void;
}) {
  const format = useFormat();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: '10px 10px 10px 14px',
        borderRadius: 16,
        backgroundColor: color.surface,
      }}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ ...texto.cuerpo, fontWeight: 700 }}>{titulo}</Text>
        <Text style={{ ...texto.pequeno, color: color.fg3 }}>
          <PrivacyFilter>{detalle}</PrivacyFilter>
        </Text>
      </View>
      <Boton
        variante="fantasma"
        isDisabled={importe <= 0}
        onPress={onPress}
        style={{ color: color.accent }}
      >
        <PrivacyFilter>{`+ ${format(importe, 'financial')}`}</PrivacyFilter>
      </Boton>
    </View>
  );
}

function ImporteDestino({
  etiqueta,
  importe,
  onCambiar,
}: {
  etiqueta: string;
  importe: number;
  onCambiar: (importe: number) => void;
}) {
  return (
    <CampoImporte
      etiqueta={etiqueta}
      valor={importe}
      onCambiar={centimos => onCambiar(centimos ?? 0)}
    />
  );
}
